import { FaWifi, FaRedo, FaServer, FaExclamationTriangle, FaSyncAlt } from "react-icons/fa";
import { Button } from "components/common";
import { Card } from "components/common/cards";
import { CUSTOMER_SUCCESS_EMAIL, CUSTOMER_SUCCESS_MAILTO } from "config/constant";
import { STATUS_PAGE_URL, formatMaintenanceEndLabel } from "utils/parseMaintenanceDetails";
import type { ErrorDisplayProps } from "./ErrorDisplay.types";

const COPY: Record<
  ErrorDisplayProps["mode"],
  {
    title: string;
    message: string;
    primaryLabel: string;
    primaryCheckingLabel?: string;
    helper?: string;
    accent: "red" | "warning";
  }
> = {
  offline: {
    title: "You're Offline",
    message:
      "Check your internet connection. RivetAI will reconnect automatically when you're back online.",
    primaryLabel: "Waiting for connection…",
    helper: undefined,
    accent: "red",
  },
  maintenance: {
    title: "Under Maintenance",
    message: "We're working on a few things. Thanks for your patience—we'll be back shortly.",
    primaryLabel: "Check now",
    primaryCheckingLabel: "Checking…",
    accent: "warning",
  },
  service_unavailable: {
    title: "Service Unavailable",
    message: "Our servers are temporarily unavailable. We're automatically trying to reconnect.",
    primaryLabel: "Check now",
    primaryCheckingLabel: "Checking…",
    helper: "You can keep this page open—we'll restore your session when the connection returns.",
    accent: "red",
  },
  react_error: {
    title: "Something went wrong",
    message:
      "An unexpected error interrupted this screen. You can try again without losing your place.",
    primaryLabel: "Try again",
    helper: "If this keeps happening, reload RivetAI or contact support.",
    accent: "red",
  },
  stale_client: {
    title: "Update required",
    message:
      "This version of RivetAI is out of date or failed to load. Reload to get the latest version.",
    primaryLabel: "Reload RivetAI",
    accent: "warning",
  },
};

export const ErrorDisplay = ({
  error,
  errorInfo,
  mode,
  isChecking = false,
  onPrimaryAction,
  onSecondaryAction,
  secondaryLabel,
  maintenanceDetails,
}: ErrorDisplayProps) => {
  const copy = COPY[mode];
  const isOffline = mode === "offline";
  const primaryDisabled =
    isOffline || (isChecking && mode !== "react_error" && mode !== "stale_client");
  const primaryLabel =
    isChecking && copy.primaryCheckingLabel ? copy.primaryCheckingLabel : copy.primaryLabel;
  const retryButtonColor = copy.accent === "warning" ? "brand" : "red";
  const iconWrapperClass =
    copy.accent === "warning"
      ? "w-16 h-16 rounded-full bg-warning/10 border-2 border-warning/20 flex items-center justify-center"
      : "w-16 h-16 rounded-full bg-danger/10 border-2 border-danger/20 flex items-center justify-center";

  const maintenanceTitle =
    mode === "maintenance" && maintenanceDetails?.title ? maintenanceDetails.title : copy.title;
  const endLabel =
    mode === "maintenance" ? formatMaintenanceEndLabel(maintenanceDetails?.endAt) : undefined;
  const maintenanceMessage =
    mode === "maintenance"
      ? [
          maintenanceDetails?.message?.trim() || copy.message,
          endLabel ? `Estimated end: ${endLabel}.` : null,
        ]
          .filter(Boolean)
          .join(" ")
      : copy.message;
  const statusPageUrl = maintenanceDetails?.statusPageUrl || STATUS_PAGE_URL;

  const getIcon = () => {
    if (mode === "react_error") {
      return <FaExclamationTriangle className="text-4xl text-danger" />;
    }
    if (mode === "offline") {
      return <FaWifi className="text-4xl text-danger" />;
    }
    if (mode === "stale_client") {
      return <FaSyncAlt className="text-4xl text-warning" />;
    }
    if (mode === "maintenance") {
      return <FaServer className="text-4xl text-warning" />;
    }
    return <FaServer className="text-4xl text-danger" />;
  };

  return (
    <Card className="p-6 text-center max-w-md mx-auto">
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          <div className={iconWrapperClass}>{getIcon()}</div>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-xl font-bold text-foreground">{maintenanceTitle}</h3>
          <p className="text-muted-foreground leading-relaxed">{maintenanceMessage}</p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={onPrimaryAction}
            disabled={primaryDisabled}
            className="w-full"
            variant="badge"
            color={retryButtonColor}
            size="lg"
          >
            <FaRedo className={`mr-2 ${isChecking ? "animate-spin-fast" : ""}`} />
            {primaryLabel}
          </Button>

          {mode === "maintenance" ? (
            <Button
              type="button"
              className="w-full"
              variant="ghostSecondary"
              size="lg"
              onClick={() => {
                window.open(statusPageUrl, "_blank", "noopener,noreferrer");
              }}
            >
              View status page
            </Button>
          ) : null}

          {onSecondaryAction && secondaryLabel ? (
            <Button
              type="button"
              onClick={onSecondaryAction}
              className="w-full"
              variant="ghostSecondary"
              size="lg"
            >
              {secondaryLabel}
            </Button>
          ) : null}

          {copy.helper ? <p className="text-sm text-muted-foreground">{copy.helper}</p> : null}
        </div>

        <div className="pt-4 border-t border-border flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Need help? Contact{" "}
            <a href={CUSTOMER_SUCCESS_MAILTO} className="underline hover:text-muted-foreground">
              {CUSTOMER_SUCCESS_EMAIL}
            </a>
            .
          </p>

          {process.env.NODE_ENV === "development" && error ? (
            <details className="text-left flex flex-col gap-1">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground/80 transition-colors">
                Technical details
              </summary>
              <div className="p-3 bg-muted rounded-lg border border-border">
                <pre className="text-xs text-danger overflow-auto whitespace-pre-wrap">
                  {error.toString()}
                  {errorInfo?.componentStack
                    ? `\n\nComponent Stack:\n${errorInfo.componentStack}`
                    : ""}
                </pre>
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </Card>
  );
};
